/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import QuillEditor from '../../../components/Quill/Quill';
import adminServices from '../../../services/adminServices';

window.katex = katex;

const Parchment = Quill.import("parchment");
const Style = new Parchment.Attributor.Style("style", "style");
Quill.register(Style, true);

const ImageWidth = new Parchment.Attributor.Attribute("width", "width", {
  scope: Parchment.Scope.INLINE,
});
Quill.register(ImageWidth, true);

const ImageHeight = new Parchment.Attributor.Attribute("height", "height", {
  scope: Parchment.Scope.INLINE,
});
Quill.register(ImageHeight, true);

const MCQMultipleSelect = ({ formData, setFormData, errors, setErrors }) => {
  const quillRefs = {
    questionText: useRef(null),
    textExplanation: useRef(null),
    options: useRef([])
  };

  const formats = React.useMemo(() => [
    "bold", "italic", "underline",
    "list", "bullet",
    "script",
    "header",
    "image",
    "formula",
    "color", "background",
    "align", "style", "width", "height"
  ], []);

  const getModules = (targetRef) => ({
    toolbar: {
      container: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ header: [1, 2, 3, false] }],
        ["image", { formula: { customClass: 'qlformula' } }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["clean"],
      ],
      handlers: {
        image: () => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.click();

          input.onchange = () => {
            if (input.files?.length) {
              handleFiles(input.files, targetRef);
            }
          };
        },
      },
    },
    clipboard: { matchVisual: false },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize', 'Toolbar']
    },
  });

  const questionModules = React.useMemo(() => getModules(quillRefs.questionText), []);
  const explanationModules = React.useMemo(() => getModules(quillRefs.textExplanation), []);

  const stableOptionModule = React.useMemo(() => ({
    toolbar: {
      container: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ header: [1, 2, 3, false] }],
        ["image", { formula: { customClass: 'qlformula' } }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["clean"],
      ],
      handlers: {
        image: () => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.click();

          input.onchange = () => {
            if (input.files?.length) {
              const activeElement = document.activeElement;
              const quillContainer = activeElement.closest('.ql-container');
              if (quillContainer) {
                const quillInstance = quillContainer.__quill;
                if (quillInstance) {
                  handleFiles(input.files, { current: { getEditor: () => quillInstance } });
                }
              }
            }
          };
        },
      },
    },
    clipboard: { matchVisual: false },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize', 'Toolbar']
    },
  }), []);

  const updateOptionRefs = React.useCallback((optionsLength) => {
    while (quillRefs.options.current.length < optionsLength) {
      quillRefs.options.current.push(React.createRef());
    }
  }, []);

  const getOptionModulesArray = React.useCallback((optionsLength) => {
    updateOptionRefs(optionsLength);
    return Array(optionsLength).fill(stableOptionModule);
  }, [updateOptionRefs, stableOptionModule]);

  const handleFiles = async (files, targetRef) => {
    const quillInstance = targetRef?.current?.getEditor();
    if (!quillInstance) return;

    for (const file of files) {
      if (file.type.startsWith("image/")) {
        const range = quillInstance.getSelection(true);
        const tempId = `temp-${Date.now()}`;
        
        // Insert loading placeholder
        const loadingGif = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='40' stroke='%234F46E5' stroke-width='8' fill='none' stroke-dasharray='60 200' stroke-linecap='round'%3E%3CanimateTransform attributeName='transform' type='rotate' dur='1s' repeatCount='indefinite' from='0 50 50' to='360 50 50'/%3E%3C/circle%3E%3C/svg%3E";
        quillInstance.insertEmbed(range.index, "image", loadingGif);
        quillInstance.setSelection(range.index + 1);

        const url = await handleUploadImage(file);
        if (url) {
          const editorEl = quillInstance.root;
          const imgElements = editorEl.querySelectorAll('img');
          const loadingImg = Array.from(imgElements).find(img => img.src === loadingGif);
          if (loadingImg) {
            loadingImg.setAttribute("src", url);
          }
        }
      }
    }
  };

  const handleUploadImage = async (file) => {
    const formData = new FormData();
    formData.append("thumbImage", file);
    try {
      const imageUrl = await adminServices.postImag(formData);
      if (imageUrl?.ok) {
        return imageUrl.data;
      }
    } catch (error) {
      console.error("Image upload failed", error);
    }
    return null;
  };

  React.useEffect(() => {
    const mountedRefs = Object.values(quillRefs).filter(ref => ref?.current);
    if (!mountedRefs.length) return;

    mountedRefs.forEach((ref) => {
      const editor = ref.current?.root;
      if (!editor) return;

      const pasteHandler = (e) => {
        const items = e.clipboardData?.items;
        if (items) {
          const files = [];
          for (const item of items) {
            if (item.type.indexOf("image") !== -1) {
              e.preventDefault();
              files.push(item.getAsFile());
            }
          }
          if (files.length) handleFiles(files, ref);
        }
      };

      const dropHandler = (e) => {
        e.preventDefault();
        const files = e.dataTransfer?.files;
        if (files?.length) handleFiles(files, ref);
      };

      editor.addEventListener("paste", pasteHandler);
      editor.addEventListener("drop", dropHandler);
      editor.addEventListener("dragover", (e) => e.preventDefault());

      return () => {
        editor.removeEventListener("paste", pasteHandler);
        editor.removeEventListener("drop", dropHandler);
      };
    });
  }, [quillRefs.questionText.current, quillRefs.textExplanation.current]);

  React.useEffect(() => {
    if (!quillRefs.options.current) {
      quillRefs.options.current = [];
    }
  }, []);

  const handleQuillChange = (field, value) => {
    setFormData(prev => ({ 
      ...prev, 
      [field]: value
    }));
    
    if (setErrors && errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (setErrors && errors?.[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleOptionChange = (index, value) => {
    setFormData(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = { 
        label: String.fromCharCode(65 + index), 
        value: value
      };
      return { ...prev, options: newOptions };
    });
    
    const optionErrorKey = `options.${index}.value`;
    if (setErrors && errors?.[optionErrorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[optionErrorKey];
        return newErrors;
      });
    }
    
    if (setErrors && errors?.options) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.options;
        return newErrors;
      });
    }
  };

  const handleCorrectAnswerToggle = (label) => {
    setFormData(prev => {
      const currentAnswers = prev.correctAnswer || [];
      let newAnswers;
      
      if (currentAnswers.includes(label)) {
        newAnswers = currentAnswers.filter(ans => ans !== label);
      } else {
        newAnswers = [...currentAnswers, label];
      }
      
      return { ...prev, correctAnswer: newAnswers };
    });
    
    if (setErrors && errors?.correctAnswer) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.correctAnswer;
        return newErrors;
      });
    }
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({
        ...prev,
        options: [
          ...prev.options, 
          { 
            label: String.fromCharCode(65 + prev.options.length), 
            value: ''
          }
        ],
      }));
    }
  };

const removeOption = (index) => {
    if (formData.options.length > 2) {
        setFormData(prev => {
            const removedLabel = prev.options[index].label;
            
            const newOptions = prev.options.filter((_, i) => i !== index);
                        const updatedOptions = newOptions.map((opt, i) => ({
                ...opt,
                label: String.fromCharCode(65 + i)
            }));

            
            const oldToNewLabel = {};
            prev.options.forEach((opt, i) => {
                if (i === index) return; 
                const newIndex = i < index ? i : i - 1;
                oldToNewLabel[opt.label] = String.fromCharCode(65 + newIndex);
            });

            const newCorrectAnswers = (prev.correctAnswer || [])
                .filter(ans => ans !== removedLabel)      
                .map(ans => oldToNewLabel[ans] || ans); 

            return {
                ...prev,
                options: updatedOptions,
                correctAnswer: newCorrectAnswers
            };
        });
    }
};
  const getOptionError = (index) => {
    return errors?.[`options.${index}.value`];
  };

  const optionModulesArray = React.useMemo(() => {
    return getOptionModulesArray(formData.options?.length);
  }, [formData.options?.length, getOptionModulesArray]);

  return (
    <div className="space-y-6" id="mcq-multi-select-form">
      {/* Question Text */}
      <div className="mb-6" id="question-text-editor">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question Text <span className="text-red-500">*</span>
        </label>
        <QuillEditor
          ref={quillRefs.questionText}
          id="quill-question-text"
          value={formData.questionText || ''}
          onChange={(value) => handleQuillChange('questionText', value)}
          theme="snow"
          modules={questionModules}
          formats={formats}
          placeholder="Enter your question here..."
        />
        {errors?.questionText && (
          <p className="mt-1 text-sm text-red-600">{errors.questionText}</p>
        )}
      </div>

      {/* Answer Options */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mt-6" id="answer-options-section">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Answer Options (Multiple Select)</h3>
          <button
            type="button"
            onClick={addOption}
            disabled={formData.options?.length >= 6}
            id="add-option-btn"
            className="inline-flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors duration-150"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Option
          </button>
        </div>

        <div className="space-y-6" id="options-list">
          {formData.options?.map((option, index) => {
            const optionError = getOptionError(index);
            return (
              <div key={index} className="space-y-2" id={`option-row-${option.label}`}>
                <div className="flex items-start gap-3">
                  {/* Checkbox for correct answers */}
                  <div className="flex items-center pt-3 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={( formData.correctAnswer || []).includes(option.label)}
                      onChange={() => handleCorrectAnswerToggle(option.label)}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded cursor-pointer"
                      title="Mark as correct answer"
                      id={`option-correct-checkbox-${option.label}`}
                    />
                  </div>

                  {/* Option label */}
                  <div className="flex items-center pt-3 flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-700 min-w-[24px] bg-white px-2 py-1 rounded border border-gray-300">
                      {option.label}
                    </span>
                  </div>

                  {/* Option editor */}
                  <div className="flex-1 min-w-0" id={`option-editor-${option.label}`}>
                    <QuillEditor
                      ref={quillRefs.options.current[index]}
                      id={`quill-option-${option.label}`}
                      value={option.value || ''}
                      onChange={(value) => handleOptionChange(index, value)}
                      theme="snow"
                      modules={optionModulesArray[index]}
                      formats={formats}
                      placeholder={`Enter option ${option.label}...`}
                    />
                    {optionError && (
                      <p className="mt-1 text-sm text-red-600">{optionError}</p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    disabled={formData.options.length <= 2}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-150 mt-1 flex-shrink-0"
                    title="Delete Option"
                    id={`option-delete-btn-${option.label}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {errors?.options && (
          <p className="mt-4 text-sm text-red-600 flex items-start gap-1">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {errors.options}
          </p>
        )}
        
        {errors?.correctAnswer && (
          <p className="mt-4 text-sm text-red-600 flex items-start gap-1">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {errors.correctAnswer}
          </p>
        )}
      </div>

      {/* Explanation Section */}
      <div className="rounded-lg border border-blue-200 p-6 mt-6" id="explanation-section">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Explanation</h3>

        {/* Text Explanation */}
        <div className="mb-6" id="explanation-editor">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Text Explanation <span className="text-red-500">*</span>
          </label>
          <QuillEditor
            ref={quillRefs.textExplanation}
            id="quill-text-explanation"
            value={formData.textExplanation || ''}
            onChange={(value) => handleQuillChange('textExplanation', value)}
            theme="snow"
            modules={explanationModules}
            formats={formats}
            placeholder="Explain the correct answers..."
          />
          {errors?.textExplanation && (
            <p className="mt-1 text-sm text-red-600">{errors.textExplanation}</p>
          )}
        </div>

        <div>
          <label htmlFor="youtubeVideoUrl" className="block text-sm font-medium text-gray-700 mb-2">
            YouTube Video Explanation (Optional)
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              id="youtubeVideoUrl"
              name="youtubeVideoUrl"
              value={formData.youtubeVideoUrl || ''}
              onChange={handleInputChange}
              placeholder="https://youtube.com/watch?v=..."
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-colors bg-white ${
                errors?.youtubeVideoUrl ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
          </div>
          {errors?.youtubeVideoUrl && (
            <p className="mt-1 text-sm text-red-600 flex items-start gap-1">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.youtubeVideoUrl}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MCQMultipleSelect;